import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
// Serialization
import { deserialize, serialize } from 'serializr';
import { slugSchema } from './schemas';
// Fragments
import { slugFragment } from './fragments';
// Permissions
import permissions from '../../constants/Permissions';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';

// Permissions
const { pFeature } = permissions;

/**
 * Serialization
 */
const deserializeSlug = (data) => deserialize(slugSchema, data);

const serializeSlug = ({
  domain, comments, slug, usages, usedOnPrintedQrCode,
}) => ({
  ...serialize(slugSchema, {
    ...(!_isNil(comments) && { comments }),
    domain,
    ...(!_isNil(slug) && { slug }),
    usages,
    ...(!_isNil(usedOnPrintedQrCode) && { usedOnPrintedQrCode }),
  }),
});

/**
 * Lists
 */
const typeList = [
  {
    key: 'default',
    value: 'default',
  },
  {
    key: 'regular',
    value: 'regular',
  },
];

const usageList = [
  {
    key: 'accounts',
    value: 'accounts',
    labelId: 'field.usageAccounts',
  },
  {
    key: 'users',
    value: 'users',
    labelId: 'field.usageUsers',
  },
];

/**
 * Utils
 */
const slugType = (data) => findInList(typeList, 'value', data, 'type');
const isRegular = (data) => _get(slugType(data), 'value') === typeList[1].value;
/**
 * GraphQL
 */
const queryPermissions = (hasPermission) => ({
  withAdmin: hasPermission({ needed: pFeature.admin }),
});

const queListSlugs = (hasPermission) => `
  query ListSlugs($pagination: PaginationInput, $search: ListSlugsSearchInput, $sort: [ListSlugsSortInput!]) {
    listSlugs(pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...slugFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${slugFragment(queryPermissions(hasPermission))}
`;

const queGetSlugByDomain = (hasPermission) => `
  query GetSlugByDomain($input: GetSlugByDomainInput!) {
    getSlugByDomain(input: $input) {
      ...slugFragment
    }
  }

  ${slugFragment(queryPermissions(hasPermission), false)}
`;

const mutCreateSlug = `
  mutation CreateSlug($input: CreateSlugInput!) {
    createSlug(input: $input) {
      id
    }
  }
`;
const mutDeleteSlug = `
  mutation DeleteSlug($input: DeleteSlugInput!) {
    deleteSlug(input: $input) {
      id
    }
  }
`;

const mutUpdateSlug = `
  mutation UpdateSlug($input: UpdateSlugInput!) {
    updateSlug(input: $input) {
      id
    }
  }
`;

/**
 * Model
 */
const slug = {
  // Serialization
  deserialize: deserializeSlug,
  serialize: serializeSlug,

  // Lists
  usageList,

  // Utils
  isRegular,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'domain', dir: 'asc' }],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 5,
      sm: 5,
      md: 5,
      lg: 5,
      xl: 5,
    }),
    columns: [
      {
        key: 'domain',
        labelId: 'field.domain',
        field: 'domain',
        sortable: true,
      },
      {
        key: 'slug',
        labelId: 'field.slug',
        field: 'slug',
        sortable: true,
      },
      {
        key: 'usages',
        labelId: 'field.usages',
        field: 'usages',
        sortable: true,
      },
      {
        key: 'comment',
        labelId: 'field.comments',
        field: 'comment',
        sortable: false,
      },
      {
        key: 'usedOnPrintedQrCode',
        labelId: 'field.usedOnPrintedQrCode',
        field: 'usedOnPrintedQrCode',
        sortable: false,
        className: 'table-content-center',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'domain',
        labelId: 'field.domain',
        field: 'domain',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'domain',
        },
      },
      {
        key: 'slug',
        labelId: 'field.slug',
        field: 'slug',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'slug',
        },
      },
      {
        key: 'workspaceId',
        labelId: '',
        field: 'workspaceId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        hidden: true,
        excludeFilterFields: ['workspaceId'],
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceId',
        },
      },
      {
        key: 'workspaceGroupId',
        labelId: '',
        field: 'workspaceGroupId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        hidden: true,
        excludeFilterFields: ['workspaceGroupId'],
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'workspaceGroupId',
        },
      },
    ],
  },

  // GraphQL
  queListSlugs,
  queGetSlugByDomain,
  mutCreateSlug,
  mutDeleteSlug,
  mutUpdateSlug,
};

module.exports = slug;



// WEBPACK FOOTER //
// ./models/cpm/slug.js